import type { EndpointDefinitions, QuerySubState } from '@reduxjs/toolkit/query';
import { PkSpinner } from '@uefadigital/panenka-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import styled from 'styled-components';

import type { RootState } from 'store/configureStore';

type QueryState<D extends EndpointDefinitions> = {
  [queryCacheKey: string]: QuerySubState<D[string]> | undefined;
};

const countQueriesInProgress = (queries: QueryState<EndpointDefinitions>) => {
  return Object.keys(queries).filter((key: string) => {
    return queries[key] && queries[key]?.status !== 'fulfilled' && queries[key]?.status !== 'rejected';
  }).length;
};

const selectFetchingQueries = createSelector(
  (state: RootState) => state.cmsApi.queries,
  (state: RootState) => state.fspMatchApi.queries,
  (state: RootState) => state.fspCompApi.queries,
  (state: RootState) => state.sponsorsApi.queries,
  (state: RootState) => state.forceLoading,
  (queriesCms, queriesFsp, queriesSponsors, forceLoading) => {
    const cms = countQueriesInProgress(queriesCms);
    const fsp = countQueriesInProgress(queriesFsp);
    const sponsors = countQueriesInProgress(queriesSponsors);

    return cms + fsp + sponsors + +forceLoading;
  },
);

export const Spinner = () => {
  const isFetching = useSelector(selectFetchingQueries);
  const { t } = useTranslation();

  return (
    <Overlay $isFetching={isFetching} className="pr-display--none">
      <SpinElement>
        <PkSpinner>
          <span slot="primary">{t('Loading')}...</span>
        </PkSpinner>
      </SpinElement>
    </Overlay>
  );
};

const Overlay = styled.div<{ $isFetching: number }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.7);
  transition: all 0.5s;
  visibility: ${(props) => (props.$isFetching > 0 ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.$isFetching > 0 ? '1' : '0')};
`;

const SpinElement = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%);
`;

export default Spinner;
